import type { ComponentNode, Decision, DecisionStatus, FieldDef, ObjectSnapshot, PageNode, Question, View } from '../../api/uiSignoff';

// Pure helpers for the UI sign-off module. What a client may see is decided by the API; these
// only shape what it returned for display.

export const MASTER = '__master';

/**
 * A section label as Salesforce shows it. Standard layout sections are stored as tokens,
 * `@@@SFDCAddress_InformationSFDC@@@`, which Salesforce translates for display; so does this.
 * Only the display changes: the label the files hold (and a content hash covers) stays as written.
 */
export function sectionTitle(raw: string): string {
  const m = /^@@@SFDC(.+)SFDC@@@$/.exec(raw.trim());
  if (!m) return raw;
  // Lightning shows this one as "Overview" (seen on the live Other and Personal Contact pages in cvdev), not the name the token suggests.
  if (m[1] === 'DandBDescription') return 'Overview';
  return m[1].replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2');
}

const ACTION_LABELS: Record<string, string> = {
  // Salesforce's own label for this standard action; it is not in any metadata, and not the name humanized.
  AccountAddToCampaign: 'Add Contacts To Campaign',
  Edit: 'Edit',
  Delete: 'Delete',
  Clone: 'Clone',
  PrintableView: 'Printable View',
  'FeedItem.ContentNote': 'New Note',
  'FeedItem.TextPost': 'Post',
  ChangeOwnerOne: 'Change Owner',
  ChangeRecordType: 'Change Record Type',
  Follow: 'Follow',
  Submit: 'Submit for Approval',
};

/**
 * The buttons a highlights panel draws, as its page names them (`actionNames`, `numVisibleActions`).
 * Standard actions have fixed labels; a quick action is named for its object (AccountAddToCampaign)
 * and shown without it. `more` is true when the page names more than it shows. Null when the page
 * does not say, so the caller keeps its own default.
 */
export function highlightActions(node: ComponentNode | null, objectApi: string): { shown: string[]; more: boolean } | null {
  const raw = node?.props.actionNames;
  if (!raw) return null;
  const labelOf = (name: string) => {
    if (ACTION_LABELS[name]) return ACTION_LABELS[name];
    const bare = (name.startsWith(objectApi) ? name.slice(objectApi.length) : name).replace(/^[A-Z]*\./, '');
    return bare.replace(/_/g, ' ').replace(/([a-z])([A-Z])/g, '$1 $2').replace(/\b(To|Of|And|For|The)\b/g, (w) => w.toLowerCase());
  };
  const names = raw.split(';').filter(Boolean);
  const visible = Number(node?.props.numVisibleActions) || names.length;
  return { shown: names.slice(0, visible).map(labelOf), more: names.length > visible };
}

/** The component of a page by name, wherever it sits. */
export function findComponent(nodes: PageNode[], names: string[]): ComponentNode | null {
  for (const n of nodes) {
    if (n.kind !== 'component') continue;
    if (names.includes(n.name)) return n;
    for (const slot of Object.values(n.slots)) {
      const hit = findComponent(slot, names);
      if (hit) return hit;
    }
  }
  return null;
}

/** The one date format the module shows: "Sep 25, 2026". */
export const day = (iso: string) => new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
/** "Sep 30, 2026, 10:04 AM EDT": a capture is a moment, not a day, so it says which zone. */
export const dayTime = (iso: string) => new Date(iso).toLocaleString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', timeZoneName: 'short' });

const TAB_TITLES: Record<string, string> = {
  'Standard.Tab.detail': 'Details',
  'Standard.Tab.candidateDetails': 'Details',
  'Standard.Tab.activity': 'Activity',
  'Standard.Tab.relatedLists': 'Related',
  'Standard.Tab.collaborate': 'Chatter',
  'Standard.Tab.chatter': 'Chatter',
  'Standard.Tab.news': 'News',
};
// A standard tab nothing above names reads as its own name ("Standard.Tab.opportunities" is "Opportunities"), not as the setup token.
const standardTab = (raw: string) => {
  const m = /^Standard\.Tab\.(\w+)$/.exec(raw);
  return m ? m[1].replace(/([a-z0-9])([A-Z])/g, '$1 $2').replace(/^./, (c) => c.toUpperCase()) : raw;
};
export const tabTitle = (raw: string | undefined) => (raw ? TAB_TITLES[raw] ?? standardTab(raw) : '');

export const recordTypeLabel = (o: ObjectSnapshot, rt: string) => (rt === MASTER ? 'Master' : o.recordTypes[rt]?.label ?? rt);

export const SOURCE_LABEL: Record<FieldDef['source'], string> = {
  overlay: 'Client overlay',
  core: 'ClientVerse Core',
  standard: 'Salesforce standard',
  org: 'The org (managed package)',
  undefined: 'Not defined in the repo',
};

export const DECISION_LABEL: Record<DecisionStatus, string> = {
  on_layout: 'On layout',
  admin_only: 'Admin only',
  hidden_phase_2: 'Hidden (Phase 2)',
  not_on_layout: 'Not on layout',
  remove: 'Remove',
  check: 'Check',
  withdrawn: 'Withdrawn',
};

// ---------- example values ----------

const PEOPLE = ['Dana Whitfield', 'Marcus Lee', 'Priya Raman', 'Tom Okafor', 'Elena Cruz'];
const COMPANIES = ['Northwind Holdings', 'Contoso Group', 'Fabrikam Inc.', 'Blue Harbor Capital', 'Adatum Corporation'];
const PERSON_OBJECTS = new Set(['User', 'Group', 'Contact', 'Lead']);
export const hashStr = (s: string) => {
  let x = 0;
  for (const c of s) x = (x * 31 + c.charCodeAt(0)) | 0;
  return Math.abs(x);
};

/**
 * Salesforce metadata stores picklist values URL-encoded ("Metals %26 Mining"); show them as the
 * org does. A value that isn't valid encoding is shown as it is.
 */
export function picklistLabel(v: string): string {
  if (!v.includes('%')) return v;
  try { return decodeURIComponent(v); } catch { return v; }
}

/** "clientverse__Practice_Area__c" → "Practice Area". */
const objectWord = (api: string) => api.replace(/^[A-Za-z0-9]+__(?=.+__c$)/, '').replace(/__c$/, '').replace(/_/g, ' ');

export type SampleValue = string | boolean | { link: string };

const STREETS = ['51 Louisiana Ave NW', '250 Vesey St', '77 W Wacker Dr', '901 Lakeside Ave', '555 California St'];
const CITIES = ['Washington, DC 20001', 'New York, NY 10281', 'Chicago, IL 60601', 'Cleveland, OH 44114', 'San Francisco, CA 94104'];

/** A field's label without its object prefix, for text built from it. */
const labelOf = (o: ObjectSnapshot, api: string) => o.fields[api]?.label ?? objectWord(api).replace(/([a-z])([A-Z])/g, '$1 $2');

/**
 * The type a field behaves as. Most come from metadata; a field the repo doesn't define ("Unknown",
 * or a formula with no stated type) is read from its name, so it still gets a value of its kind.
 */
function kindOf(type: string, api: string): string {
  const t = type.toLowerCase();
  if (t && t !== 'unknown' && t !== 'formula') return t;
  const n = api.replace(/__c$/, '');
  if (/phone|fax|mobile/i.test(n)) return 'phone';
  if (/email/i.test(n)) return 'email';
  if (/website|url|linkedin/i.test(n)) return 'url';
  if (/date$|_date|Date_/i.test(n)) return 'date';
  if (/address|street/i.test(n)) return 'address';
  if (/amount|revenue|budget|cost|price|fee/i.test(n)) return 'currency';
  if (/count|number_of|employees|rank|quantity/i.test(n)) return 'number';
  if (/^(is|has)[A-Z_]/.test(n)) return 'checkbox';
  return 'text';
}

/**
 * A plausible value for a field, deterministic by object and field name so the page doesn't
 * change between renders. Never client data: every value is generated here. Every field gets one,
 * of its type: a blank on a review page reads as a field with nothing to show, which is a
 * different thing to sign off.
 */
export function sampleValue(o: ObjectSnapshot, api: string, rt: string): SampleValue {
  const f = o.fields[api];
  const t = kindOf(f?.type ?? '', api);
  const k = hashStr(`${o.api}.${api}`);
  const label = labelOf(o, api);
  if (api === 'Name') return `Example ${o.label}`;
  // The record type is the one this view is for, never an example.
  if (api === 'RecordTypeId') return rt === MASTER ? 'Master' : o.recordTypes[rt]?.label ?? rt;
  if (t.includes('checkbox') || t === 'boolean') return k % 3 === 0;
  if (t.includes('date')) {
    // Ends fall after starts: an end, close or expiry date is in October, everything else in September.
    const later = /(^|_)(end|close|expir|due)|(End|Close|Expir|Due)/.test(api);
    const d = `${later ? 10 : 9}/${(k % 28) + 1}/2026`;
    return t.includes('datetime') ? `${d}, 9:00 AM` : d;
  }
  if (t === 'time') return `${(k % 8) + 9}:00 AM`;
  if (t.includes('currency')) return `$${(((k % 900) + 100) * 50).toLocaleString('en-US')}.00`;
  if (t.includes('percent')) return `${(k % 80) + 10}%`;
  if (t === 'summary') return String((k % 40) + 1);
  if (t === 'autonumber') return `${o.label.slice(0, 1).toUpperCase()}-${String((k % 90000) + 10000).padStart(6, '0')}`;
  if (t.includes('number') || t.includes('double') || t.includes('int')) return String((k % 300) + 1);
  if (t.includes('picklist')) {
    const vals = (o.recordTypes[rt]?.picklists[api] ?? f?.picklist ?? []).map(picklistLabel);
    // No values in the repo (a global value set, say): still a value, and plainly an example.
    const pick = (i: number) => vals.length ? vals[i % vals.length] : `Example ${label} ${String.fromCharCode(65 + (i % 3))}`;
    if (t.includes('multi')) return [...new Set([pick(k), pick(k + 1)])].join('; ');
    return pick(k);
  }
  if (t.includes('lookup') || t.includes('masterdetail') || t.includes('hierarchy') || f?.referenceTo?.length) {
    // A hierarchy field (Parent Company) points at its own object; an unknown lookup at a user.
    const target = f?.referenceTo?.[0] ?? (t.includes('hierarchy') || /^Parent(Id)?$/.test(api) ? o.api : 'User');
    if (PERSON_OBJECTS.has(target)) return { link: PEOPLE[k % PEOPLE.length] };
    if (target === 'Account') return { link: COMPANIES[k % COMPANIES.length] };
    if (target === o.api) return { link: `Example Parent ${o.label}` };
    return { link: `Example ${objectWord(target)}` };
  }
  if (t === 'url') return { link: 'example.com' };
  if (t === 'email') return 'name@example.com';
  if (t === 'phone') return '(212) 555-0100';
  if (t === 'address') return `${STREETS[k % STREETS.length]}, ${CITIES[k % CITIES.length]}`;
  if (t === 'location') return '38.8977, -77.0365';
  if (t === 'encryptedtext') return `••••${String(k % 10000).padStart(4, '0')}`;
  if (t.includes('textarea') || t === 'html') return `Example ${label.toLowerCase()} for this ${o.label.toLowerCase()}.`;
  // Text: an identifier-like field gets a code, anything else an example of itself.
  if (/(number|code|\bid\b|ticker|symbol)$/i.test(label)) {
    // "Client Group Number" → CG-12345; a one-word stem, "Client Number" → CL-12345.
    const words = label.split(/[^A-Za-z]+/).filter((w) => w && !/^(number|code|id|ticker|symbol)$/i.test(w));
    const prefix = (words.length > 1 ? words.map((w) => w[0]).join('').slice(0, 3) : (words[0] ?? 'EX').slice(0, 2)).toUpperCase();
    return `${prefix}-${(k % 90000) + 10000}`;
  }
  if (/names?$/i.test(label)) return `Example ${label.replace(/s$/, '')}`;
  return `Example ${label}`;
}

// ---------- related list sample rows ----------

/** One column of a related list: what to head it with and how to fill it. */
export interface RelatedCol { token: string; label: string; type: string; referenceTo?: string[] }

type StdCol = { label: string; type: string };
/**
 * Columns a layout names with Salesforce's own tokens, headed as Salesforce heads them. `person`,
 * `company`, `name` and `status` are filled here; every other type goes through sampleValue.
 */
const STANDARD_COLUMNS: Record<string, StdCol> = {
  NAME: { label: 'Name', type: 'name' },
  FULL_NAME: { label: 'Name', type: 'person' },
  'CONTACT.FULL_NAME': { label: 'Name', type: 'person' },
  'CONTACT.TITLE': { label: 'Title', type: 'text' },
  'CONTACT.EMAIL': { label: 'Email', type: 'email' },
  COMPANY: { label: 'Company', type: 'company' },
  TITLE: { label: 'Title', type: 'text' },
  EMAIL: { label: 'Email', type: 'email' },
  PHONE1: { label: 'Phone', type: 'phone' },
  STATUS: { label: 'Status', type: 'status' },
  MEMBER_TYPE: { label: 'Type', type: 'membertype' },
  CREATED_DATE: { label: 'Created Date', type: 'date' },
  LAST_UPDATE: { label: 'Last Modified', type: 'date' },
  LASTMODIFIEDDATE: { label: 'Last Modified', type: 'date' },
  LASTMODIFIEDBY: { label: 'Last Modified By', type: 'person' },
  CREATEDBY_USER: { label: 'Created By', type: 'person' },
  'CORE.USERS.FULL_NAME': { label: 'Assigned To', type: 'person' },
  'TASK.SUBJECT': { label: 'Subject', type: 'text' },
  'TASK.WHO_NAME': { label: 'Name', type: 'person' },
  'TASK.DUE_DATE': { label: 'Due Date', type: 'date' },
  'TASK.STATUS': { label: 'Status', type: 'status' },
  'TASK.PRIORITY': { label: 'Priority', type: 'priority' },
  'TASK.LAST_UPDATE': { label: 'Last Modified', type: 'date' },
  'ACTIVITY.TASK': { label: 'Task', type: 'checkbox' },
  'ACCCONRELATION.ROLES': { label: 'Roles', type: 'text' },
  'OPPORTUNITY.NAME': { label: 'Opportunity Name', type: 'name' },
  'OPPORTUNITY.STAGE_NAME': { label: 'Stage', type: 'status' },
  'OPPORTUNITY.AMOUNT': { label: 'Amount', type: 'currency' },
  'OPPORTUNITY.CLOSE_DATE': { label: 'Close Date', type: 'date' },
  'ACCOUNT.NAME': { label: 'Name', type: 'company' },
  ISDEFAULT: { label: 'Default', type: 'checkbox' },
  HASRESPONDED: { label: 'Responded', type: 'checkbox' },
  LABEL: { label: 'Label', type: 'text' },
  'HISTORY.DATE': { label: 'Date', type: 'date' },
  'HISTORY.FIELD': { label: 'Field', type: 'text' },
  'HISTORY.USER': { label: 'User', type: 'person' },
  'HISTORY.ORIGINAL': { label: 'Original Value', type: 'text' },
  'HISTORY.NEW': { label: 'New Value', type: 'text' },
  'NOTE.TITLE': { label: 'Title', type: 'text' },
  'FILE.SIZE': { label: 'Size', type: 'size' },
  'CAMPAIGN.NAME': { label: 'Campaign Name', type: 'name' },
  'CAMPAIGN.TYPE': { label: 'Type', type: 'text' },
  'CAMPAIGN.STATUS': { label: 'Status', type: 'status' },
  'CAMPAIGN.START_DATE': { label: 'Start Date', type: 'date' },
};

/** What a list shows when no layout or page says: enough columns to read as the kind of list it is. */
const DEFAULT_COLUMNS: Record<string, string[]> = {
  Histories: ['HISTORY.DATE', 'HISTORY.FIELD', 'HISTORY.USER', 'HISTORY.ORIGINAL', 'HISTORY.NEW'],
  OpportunityHistories: ['HISTORY.DATE', 'HISTORY.FIELD', 'HISTORY.USER', 'HISTORY.ORIGINAL', 'HISTORY.NEW'],
  AttachedContentNotes: ['NOTE.TITLE', 'LAST_UPDATE', 'CREATEDBY_USER'],
  AttachedContentDocuments: ['NOTE.TITLE', 'FILE.SIZE', 'LAST_UPDATE', 'CREATEDBY_USER'],
  CampaignMembers: ['FULL_NAME', 'COMPANY', 'TITLE', 'STATUS'],
  ActivityHistories: ['TASK.SUBJECT', 'TASK.WHO_NAME', 'TASK.DUE_DATE', 'CORE.USERS.FULL_NAME'],
  OpenActivities: ['TASK.SUBJECT', 'TASK.WHO_NAME', 'TASK.DUE_DATE', 'TASK.STATUS'],
  Campaigns: ['CAMPAIGN.NAME', 'CAMPAIGN.TYPE', 'CAMPAIGN.STATUS', 'CAMPAIGN.START_DATE'],
};

const titleCase = (s: string) => s.toLowerCase().split('_').filter(Boolean).map((w) => w[0].toUpperCase() + w.slice(1)).join(' ');

/** A token as a column: Salesforce's own heading, the repo's label and type, or a heading read from its name. */
function relatedColumn(o: ObjectSnapshot, token: string): RelatedCol {
  const std = STANDARD_COLUMNS[token];
  if (std) return { token, ...std };
  const known = o.relatedFields?.[token];
  if (known) return { token, label: known.label, type: known.type, ...(known.referenceTo ? { referenceTo: known.referenceTo } : {}) };
  const api = token.slice(token.lastIndexOf('.') + 1);
  const label = /__[cr]$/.test(api) ? objectWord(api.replace(/__r$/, '__c')).replace(/([a-z])([A-Z])/g, '$1 $2') : titleCase(api.replace(/([a-z])([A-Z])/g, '$1_$2'));
  return { token, label, type: kindOf('', api) };
}

/**
 * The columns a related list on this page shows for this view. A dynamic related list names them
 * on the page; a standard one takes them from the view's layout; failing both, a default for the
 * kind of list, and a bare Name where it is not a kind we know.
 */
export function relatedColumns(o: ObjectSnapshot, v: View, node: ComponentNode): RelatedCol[] {
  const api = node.props.relatedListApiName ?? '';
  const named = (node.props.relatedListFieldAliases ?? '').split(';').filter(Boolean);
  const layout = v.layout ? o.layouts[v.layout]?.relatedColumns?.[api] : undefined;
  const tokens = named.length ? named : layout?.length ? layout : DEFAULT_COLUMNS[api] ?? ['NAME'];
  return tokens.map((t) => relatedColumn(o, t));
}

const STATUSES: Record<string, string[]> = {
  CampaignMembers: ['Responded', 'Sent', 'Planned'],
  ActivityHistories: ['Completed', 'Completed', 'Completed'],
  OpenActivities: ['Not Started', 'In Progress', 'Waiting'],
};
const GENERIC_STATUS = ['Active', 'Pending', 'Complete'];

/** "Campaign Members" → "Campaign Member", for "Example Campaign Member 1". */
const singular = (s: string) => s.replace(/ies$/i, 'y').replace(/s$/i, '');

/**
 * Three example rows for a related list (fewer where the page asks to show fewer), one cell per
 * column. Deterministic by list and column so a page doesn't change between renders; never client
 * data. A custom column goes through sampleValue as a field of its own type.
 */
export function relatedRows(o: ObjectSnapshot, listLabel: string, node: ComponentNode, cols: RelatedCol[]): SampleValue[][] {
  const api = node.props.relatedListApiName ?? '';
  const asked = Number(node.props.maxRecordsToDisplay ?? node.props.rowsToDisplay);
  const count = Math.max(1, Math.min(3, Number.isFinite(asked) && asked > 0 ? asked : 3));
  return Array.from({ length: count }, (_, row) =>
    cols.map((c) => {
      const k = hashStr(`${api}.${c.token}`) + row;
      const type = c.type.toLowerCase();
      switch (type) {
        case 'person': return { link: PEOPLE[k % PEOPLE.length] };
        case 'company': return { link: COMPANIES[k % COMPANIES.length] };
        case 'name': return { link: `Example ${singular(listLabel)} ${row + 1}` };
        case 'status': return (STATUSES[api] ?? GENERIC_STATUS)[row % 3];
        case 'membertype': return row === 2 ? 'Lead' : 'Contact';
        case 'priority': return ['Normal', 'High', 'Normal'][row % 3];
        case 'size': return ['2.1 MB', '480 KB', '1.3 MB'][row % 3];
        default: {
          // A stand-in object per row and column, so sampleValue varies the value by row.
          const api2 = c.token.slice(c.token.lastIndexOf('.') + 1);
          const stand: ObjectSnapshot = { ...o, api: `${api}#${row}`, label: singular(listLabel), recordTypes: {}, fields: { [api2]: { label: c.label, type: c.type, ...(c.referenceTo ? { referenceTo: c.referenceTo } : {}), source: 'undefined' } } };
          return sampleValue(stand, api2, MASTER);
        }
      }
    }),
  );
}

// ---------- page walking ----------

export function* walk(items: PageNode[]): Generator<PageNode> {
  for (const n of items) {
    yield n;
    if (n.kind === 'component') for (const s of Object.values(n.slots)) yield* walk(s);
  }
}

export const pageComponents = (o: ObjectSnapshot, v: View): ComponentNode[] =>
  v.page && o.pages[v.page] ? o.pages[v.page].regions.flatMap((r) => [...walk(r.items)]).filter((n): n is ComponentNode => n.kind === 'component') : [];

/** Section labels on a view: layout sections plus Dynamic Forms field sections. */
export function viewSections(o: ObjectSnapshot, v: View): string[] {
  const fromLayout = v.layout ? (o.layouts[v.layout]?.sections ?? []).map((s) => s.label) : [];
  const fromPage = pageComponents(o, v).filter((c) => c.name === 'flexipage:fieldSection').map((c) => c.props.label ?? '');
  return [...fromLayout, ...fromPage];
}

// ---------- decisions overlay ----------

export interface Overlay {
  byField: Map<string, Decision>;
  /** Agreed fields not yet in the repo, keyed by placement (lower-case section label or "highlights"). */
  pending: Map<string, Decision[]>;
  openQuestions: Map<string, Question[]>;
}

export function buildOverlay(current: Decision[], questions: Question[]): Overlay {
  const byField = new Map<string, Decision>();
  const pending = new Map<string, Decision[]>();
  for (const d of current) {
    if (d.fieldApi) byField.set(d.fieldApi, d);
    else if (d.status === 'on_layout') {
      const k = (d.placement ?? '').toLowerCase();
      pending.set(k, [...(pending.get(k) ?? []), d]);
    }
  }
  const openQuestions = new Map<string, Question[]>();
  for (const q of questions) if (q.status !== 'resolved') openQuestions.set(q.anchorRef, [...(openQuestions.get(q.anchorRef) ?? []), q]);
  return { byField, pending, openQuestions };
}

export type Badge = { kind: 'remove' | 'admin' | 'question' | 'agreed' | 'phase2'; text: string } | null;

export function badgeFor(api: string, ov: Overlay): Badge {
  const d = ov.byField.get(api);
  if (d?.status === 'remove') return { kind: 'remove', text: 'Remove' };
  if (d?.status === 'admin_only') return { kind: 'admin', text: 'Admin' };
  if (d?.status === 'hidden_phase_2') return { kind: 'phase2', text: 'Phase 2' };
  if (ov.openQuestions.has(api)) return { kind: 'question', text: String(ov.openQuestions.get(api)!.length) };
  if (d && (d.status === 'on_layout' || d.note)) return { kind: 'agreed', text: '✓' };
  return null;
}

/** What a sign-off on this view would carry, mirroring exceptionsFor() in the API. */
export function exceptionsPreview(o: ObjectSnapshot, v: View, ov: Overlay) {
  const shown = new Set(v.fields);
  const sections = new Set(viewSections(o, v).map((s) => s.toLowerCase()));
  return {
    openQuestions: [...ov.openQuestions.entries()].filter(([f]) => shown.has(f)).flatMap(([, qs]) => qs),
    agreedNotBuilt: [...ov.pending.entries()].filter(([k]) => k === '' || k === 'highlights' || sections.has(k)).flatMap(([, ds]) => ds),
    removalsStillPresent: [...ov.byField.values()].filter((d) => d.status === 'remove' && shown.has(d.fieldApi!)),
  };
}

// ---------- statement ----------

/** Fills {object}, {persona}, {record type}, {repo} and {sha7} in the configured statement. */
export function fillStatement(text: string, vars: { object: string; persona: string; recordType: string; repo: string; commit: string }): string {
  return text
    .replace(/\{object\}/g, vars.object)
    .replace(/\{persona\}/g, vars.persona)
    .replace(/\{record type\}/g, vars.recordType)
    .replace(/\{repo\}/g, vars.repo)
    .replace(/\{sha7\}/g, vars.commit.slice(0, 7));
}

/** "A", "A and B", "A, B, and C": each name once, in the order first seen. */
export function listOf(names: string[]): string {
  const xs = [...new Set(names)];
  return xs.length < 3 ? xs.join(' and ') : `${xs.slice(0, -1).join(', ')}, and ${xs[xs.length - 1]}`;
}

/**
 * The statement filled for one page of a capture, exactly as the API fills it when the page is
 * signed (mi-portal-api uiSignoff/logic.ts `fillPageStatement`): the signer reads what is stored.
 */
export function fillPageStatement(text: string, page: { objectLabel: string; views: { persona: string; recordTypeLabel: string }[] }, repo: string, commit: string): string {
  return text
    .replace(/\{object\}/g, page.objectLabel)
    .replace(/\{persona\}/g, listOf(page.views.map((v) => v.persona)))
    .replace(/\{record type\}/g, listOf(page.views.map((v) => v.recordTypeLabel)))
    .replace(/\{repo\}/g, repo)
    .replace(/\{sha7\}/g, commit.slice(0, 7));
}

/** The exceptions of every view a page serves, each once. */
export function pageExceptionsPreview(o: ObjectSnapshot, views: View[], ov: Overlay) {
  const all = views.map((v) => exceptionsPreview(o, v, ov));
  const once = <T extends { id: string }>(xs: T[]) => [...new Map(xs.map((x) => [x.id, x])).values()];
  return {
    openQuestions: once(all.flatMap((x) => x.openQuestions)),
    agreedNotBuilt: once(all.flatMap((x) => x.agreedNotBuilt)),
    removalsStillPresent: once(all.flatMap((x) => x.removalsStillPresent)),
  };
}

export const visibilityText =(n: { visibility?: { criteria: { left: string; operator: string; right: string | null }[]; booleanFilter?: string } }) =>
  n.visibility
    ? n.visibility.criteria.map((c) => `${c.left.replace(/^\{!Record\.|\}$/g, '').replace(/^\{!|\}$/g, '')} ${c.operator === 'EQUAL' ? '=' : c.operator} ${c.right ?? ''}`).join(n.visibility.booleanFilter ? ` (${n.visibility.booleanFilter}) ` : ' and ')
    : null;
